"use client";

/**
 * Navbar.tsx
 *
 * The main header component for the Royal Blue theme.
 *
 * Features:
 *  - Responsive design (different layouts for desktop/mobile)
 *  - Scroll-aware transparency (background changes as user scrolls)
 *  - Active section highlighting using IntersectionObserver
 *  - User profile menu integration
 *  - Full-screen info drawer toggle
 *
 * Sub-components:
 *  - Logo (text/icon support)
 *  - DesktopLinks (horizontal menu)
 *  - UserMenu (global integration)
 *  - SideDrawer (full information panel)
 *  - MobileMenuAccordion (expandable links on small screens)
 */

import React, { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAppSelector } from "@/store/hooks";
import { Menu, X } from "lucide-react";

import SideDrawer from "./SideDrawer";
import NavbarLogo from "./NavbarLogo";
import DesktopLinks from "./DesktopLinks";
import MobileMenuAccordion from "./MobileMenuAccordion";
import { GlobalUserMenu } from "@/components/global/GlobalUserMenu";

// ---------------------------------------------------------------------------
// Constants & Defaults
// ---------------------------------------------------------------------------

const DEFAULT_LINKS = [
  { name: "Home", href: "#home" },
  { name: "About", href: "#about" },
  { name: "Menu", href: "/menu" },
  { name: "Events", href: "#events" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Fixes hash links (e.g. "#about") to work correctly when visited
 * from a page other than the homepage (e.g. "/events").
 */
function resolveHref(href: string, currentPath: string): string {
  if (href.startsWith("#") && currentPath !== "/") {
    return `/${href}`;
  }
  return href;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSegment, setActiveSegment] = useState("home");

  const pathname = usePathname();
  const { data: content } = useAppSelector((state: any) => state.content);
  const data = content?.navbar;

  // Prepare links by resolving hash paths relative to the current URL
  const navLinks = useMemo(() => {
    const raw = data?.links || DEFAULT_LINKS;
    return raw.map((link: any) => ({
      ...link,
      href: resolveHref(link.href, pathname),
    }));
  }, [data?.links, pathname]);

  const reservation = data?.reservationButton || {
    label: "Reservation",
    href: "#reservation",
  };
  const reservationHref = resolveHref(reservation.href, pathname);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const toggleMobileMenu = () => {
    if (window.innerWidth >= 1024) {
      setIsDrawerOpen(true); // Large screen menu toggle opens the drawer
    } else {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    }
  };

  // ── Scroll & Intersection Observer Layer ─────────────────────────────────

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const observerOptions = {
      root: null,
      rootMargin: "-25% 0px -25% 0px", // Middle 50% of the viewport triggers intersection
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSegment(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    // Track sections mentioned in navigation links
    const sectionIds = navLinks
      .map((link: any) => {
        const href = link.href;
        if (href.startsWith("#")) return href.slice(1);
        if (href.includes("#")) return href.split("#")[1];
        return null;
      })
      .filter(Boolean);

    sectionIds.forEach((id: string) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, [navLinks, pathname]);

  // CSS classes for navbar background transition
  const navClass =
    isScrolled || pathname !== "/"
      ? "bg-[#1a1a1a]/95 backdrop-blur-md border-white/10 h-20"
      : "bg-transparent border-white/20 h-24";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 border-b ${navClass} ${
          isMobileMenuOpen ? "bg-[#1a1a1a]" : ""
        }`}
      >
        <div className="container mx-auto px-6 md:px-20 h-full flex items-center justify-between">
          <NavbarLogo icon={data?.logo?.icon} text={data?.logo?.text} />

          {/* Menus & Actions Container */}
          <div className="flex h-full items-center">
            <DesktopLinks
              links={navLinks}
              pathname={pathname}
              activeSection={activeSegment}
            />

            <div className="flex items-center space-x-4 md:space-x-6 h-full">
              {/* Online Booking Button */}
              <Link
                href={reservationHref}
                className="hidden sm:flex items-center h-10 px-4 border border-white text-white text-[14px] font-bold font-inter tracking-wide hover:bg-white hover:text-black transition-all duration-300"
              >
                {reservation.label}
              </Link>

              {/* User Account / Profile */}
              {data?.showUserIcon !== false && (
                <div className="flex items-center">
                  <GlobalUserMenu position="inline" />
                </div>
              )}

              {/* Hamburger Toggle (Side information) */}
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="lg:hidden text-white hover:text-[#c59d5f] transition-colors focus:outline-none"
                aria-label="Toggle information"
              >
                <div className="flex flex-col space-y-[5px] w-6">
                  <span className="block h-0.5 w-full bg-current" />
                  <span className="block h-0.5 w-3/5 bg-current" />
                  <span className="block h-0.5 w-full bg-current" />
                </div>
              </button>

              {/* Hamburger Toggle (Main Navigation) */}
              <button
                onClick={toggleMobileMenu}
                className="text-white hover:text-[#c59d5f] transition-colors focus:outline-none"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X size={24} strokeWidth={2} className="lg:hidden" />
                ) : (
                  <Menu size={24} strokeWidth={2} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Mobile menu (Expandable Accordion) ── */}
        <MobileMenuAccordion
          isOpen={isMobileMenuOpen}
          links={navLinks}
          reservationHref={reservationHref}
          reservationLabel={reservation.label}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      </nav>

      {/* ── Side drawer for extra brand information ── */}
      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
};

export default Navbar;
